import { create } from 'zustand';

/** Per-factor sub-scores backing a grade (each already scaled to its weight). */
export interface BosGradeFactors {
  displacement: number;
  volume: number;
  closeThrough: number;
  trendAlignment: number;
  liquiditySweep: number;
  premiumDiscount: number;
  swingSignificance: number;
  session: number;
  retest: number;
}

export interface BosMarker {
  timestamp: number;
  symbol: string;
  direction: 'bullish' | 'bearish';
  breakLevel: number;
  swingTimestamp: number;
  markerType: 'bos';
  /** Structure classification: 'bos' (continuation) or 'choch' (reversal) */
  structureType: 'bos' | 'choch';
  skipped: boolean;
  skipReason?: string | null;
  /** Resolved grade (includes retest outcome): 'A+'|'A'|'B+'|'B'|'F'. */
  grade?: string;
  /** Resolved 0..100 score (includes retest bonus). */
  score?: number;
  /** Predictive grade at the break candle — lookahead-free, no retest bonus. */
  baseGrade?: string;
  /** Predictive 0..100 score at the break candle (no retest bonus). */
  baseScore?: number;
  /** Retest phase status: 'pending' | 'held' | 'failed' | 'none'. */
  retestStatus?: string;
  /** Per-factor sub-scores backing the grade. */
  factors?: BosGradeFactors;
  /**
   * Preliminary/unconfirmed instant fire (true) vs confirmed at close (false).
   * A preliminary marker is later replaced in place by its confirmation, which
   * shares the same `id`.
   */
  pending?: boolean;
  /** Stable break identifier — a preliminary and its confirmation share this. */
  id?: string;
}

interface BosState {
  markers: BosMarker[];
  addMarker: (m: BosMarker) => void;
  removeMarker: (id: string) => void;
  setMarkers: (m: BosMarker[]) => void;
  clearMarkers: () => void;
}

export const useBosStore = create<BosState>((set) => ({
  markers: [],
  addMarker: (m) =>
    set((state) => {
      // Prefer upsert by stable `id` so a confirmed marker (pending=false)
      // cleanly REPLACES its earlier preliminary (pending=true) version, which
      // carries the same id. Fall back to the legacy identity key
      // (symbol, swingTimestamp, direction) when no id is present so retest
      // updates on older markers still replace in place rather than duplicate.
      const idx = state.markers.findIndex((x) =>
        m.id != null && x.id != null
          ? x.id === m.id
          : x.symbol === m.symbol &&
            x.swingTimestamp === m.swingTimestamp &&
            x.direction === m.direction
      );
      if (idx === -1) {
        return {
          markers: [...state.markers, m].sort((a, b) => a.timestamp - b.timestamp),
        };
      }
      const next = state.markers.slice();
      next[idx] = m;
      return { markers: next };
    }),
  removeMarker: (id) =>
    set((state) => ({
      markers: state.markers.filter((x) => x.id !== id),
    })),
  setMarkers: (markers) =>
    set({ markers: [...markers].sort((a, b) => a.timestamp - b.timestamp) }),
  clearMarkers: () => set({ markers: [] }),
}));
